"use client";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components-shared/ui/carousel";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Fade from "embla-carousel-fade";
import { Badge } from "@/components-shared/ui/badge";
import { H2 } from "@/components-shared/headers";
import _360deg from "@/public/images/360deg.svg";
import { useBoolean } from "usehooks-ts";
import useSizes from "@/lib/window-sizes-hook";
import UnitIcon from "@/components-shared/unit-icon";
import FavoriteBtn from "@/components-shared/favorite-btn";
import useThumb from "../utils/useThumb";
import { useCities } from "@/lib/cities.data";
import { useParams, useSearchParams } from "next/navigation";
import { dir } from "i18next";
import { useLocale, useTranslations } from "next-intl";
import DisplayVideos from "./DisplayVideos";
import ChatBtn from "@/components-shared/chat-btn";
import View360 from "./View360";
import { differenceInDays, format } from "date-fns";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import config from "@/lib/config";
import { cn, roundTo1Decimal } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { CircleAlert } from "lucide-react";
import CircularProgress from "@/components-shared/CircularProgress";
import Image from "next/image";
import { Skeleton } from "@/components-shared/ui/skeleton";

const CarouselWithThumbs = ({ data }: { data?: Property }) => {
  const { maxSm } = useSizes();
  const isClient = useSelector((state: RootState) => state.isBrowser);
  const searchParams = useSearchParams();
  const { unit } = useParams();
  const IsSmallScreen = useMemo(() => maxSm && isClient, [maxSm, isClient]);

  const t = useTranslations();
  const [estimation, setEstimation] = useState<EstimationType>(null);

  const priceOfToday = useMemo(
    () => ({
      original: Number(data?.originalPrice),
      current: Number(data?.totalPrice),
    }),
    [data?.originalPrice, data?.totalPrice]
  );

  const discount = useMemo(
    () =>
      data?.pricingRules?.[0]?.value
        ? Math.abs(Number(data?.pricingRules[0]?.value))
        : Math.abs(
            100 -
              Math.round(
                (Number(priceOfToday.current) / Number(priceOfToday.original)) *
                  100
              )
          ),
    [priceOfToday]
  );

  const duration = useMemo(() => {
    if (searchParams.get("checkInDate") && searchParams.get("checkOutDate")) {
      return differenceInDays(
        new Date(searchParams.get("checkOutDate")!),
        new Date(searchParams.get("checkInDate")!)
      );
    } else {
      return 1;
    }
  }, [searchParams]);

  const getEstimation = useCallback(
    async (date: { from: Date | string; to?: Date | string }) => {
      try {
        const pricesRes = await ehjzleeInstance.post(
          `${config.props}/${unit}/estimation`,
          {
            checkInDate: format(new Date(date.from!), "yyyy-MM-dd"),
            checkOutDate: format(date.to!, "yyyy-MM-dd"),
          }
        );

        setEstimation(pricesRes.data.estimate);
      } catch (error) {
        console.log(error);
      }
    },
    [unit]
  );

  useEffect(() => {
    if (searchParams.get("checkInDate") && searchParams.get("checkOutDate")) {
      getEstimation({
        from: new Date(searchParams.get("checkInDate")!),
        to: new Date(searchParams.get("checkOutDate")!),
      });
    }
  }, [searchParams, getEstimation]);

  if (IsSmallScreen)
    return <CarouselWithThumbs_sm {...{ data, t, discount }} />;

  return (
    <CarouselWithThumbs_lg
      {...{ data, t, duration, estimation, discount, priceOfToday }}
    />
  );
};

export default CarouselWithThumbs;

const CarouselWithThumbs_sm = ({
  data,
  t,
  discount,
}: {
  data?: Property;
  t: any;
  discount: number;
}) => {
  const { onThumbClick, selectedIndex, setApiMain, setApiThumb } = useThumb();
  const isFavorite = useBoolean(data?.isFavorite);
  const isLoading = useBoolean(false);
  const locale = useLocale();
  const { data: cities } = useCities();

  return (
    <>
      <div className="flex justify-between gap-3 flex-wrap mb-3">
        <div className="flex gap-4 items-center ">
          <UnitIcon
            type={data?.classification}
            className="[&_path]:fill-green-main size-6"
          />
          <H2 className="text-green-main">
            {data?.title ?? t("carousel.defaultTitle")}
          </H2>
        </div>
        <p>
          {data?.location?.address ?? ""}،{" "}
          {
            cities?.find((c) => c?.id === data?.cityId)?.[
              locale === "ar" ? "arabicName" : "name"
            ]
          }
        </p>
      </div>

      <Carousel
        setApi={setApiMain}
        opts={{ loop: true, direction: dir(locale) }}
        plugins={[Fade()]}
        className="rounded-2xl relative overflow-hidden h-[230px] sm:h-[650px]"
      >
        <CarouselContent>
          {data?.media
            ?.filter((p) => p.type === "IMAGE")
            ?.map((img, i) => (
              <ImageContainer key={i} {...img} />
            ))}
        </CarouselContent>
        <div className="absolute inset-0 py-4 flex flex-col justify-between items-start w-full">
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0)_77.36%,_rgba(0,_0,_0,_0.4)_85.02%)] rounded-2xl" />
          <div className="flex justify-between items-start w-full">
            <div className="flex px-2 items-center justify-between w-full">
              {discount ? (
                !data?.pricingRules?.length ? (
                  <Badge>
                    {t("carousel.discount")} {Math.abs(discount)}%
                  </Badge>
                ) : null
              ) : null}{" "}
              <div className="relative z-10 flex gap-2 *:bg-black/50 *:rounded-full">
                <ChatBtn unitId={data?.id!} ownerCode={data?.owner?.code!} />
                <FavoriteBtn
                  isLoading={isLoading}
                  id={data?.id!}
                  isFavorite={isFavorite}
                />
                {data?.view360Url && <View360 view360url={data?.view360Url} />}
              </div>
            </div>

            <div className="text-white space-y-3 flex-1 relative z-10">
              <div className="flex gap-2">
                {data?.ehjzleeOperated && (
                  <Badge>{t("carousel.managedByEhjzlee")} </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="z-10 px-2">
            <DisplayVideos
              videos={data?.media.filter((p) => p.type === "VIDEO")}
            />
          </div>
        </div>
      </Carousel>
      {data?.pricingRules?.length ? (
        <Badge className="mt-2 font-semibold">
          {t("carousel.discountPeriod", {
            discount: `${Math.abs(discount)} ${
              data?.pricingRules?.[0]?.type === "ABSOLUTE_DIFFERENCE"
                ? t("propCard.current")
                : "%"
              }`,
            endPeriod: format(
              new Date(data?.pricingRules[0].endDate),
              "dd/MM/yyyy"
            ),
          })}
        </Badge>
      ) : null}
      <Carousel
        setApi={setApiThumb}
        orientation="horizontal"
        className="flex items-center w-full gap-3 mt-4"
        opts={{
          containScroll: "keepSnaps",
          slidesToScroll: 1,
          direction: dir(locale),
        }}
      >
        <CarouselPrevious className="size-8 relative rtl:rotate-180 p-0 border-none !bg-transparent [&>svg]:stroke-gray-dark [&>svg]:disabled:stroke-gray-400 " />
        <CarouselContent className="flex gap-2 h-14">
          {data?.media
            ?.filter((p) => p.type === "IMAGE")
            ?.map((img, i) => (
              <ThumbnailContainer
                i={i}
                key={i}
                img={img}
                onThumbClick={onThumbClick}
                selectedIndex={selectedIndex}
                size={55}
              />
            ))}
        </CarouselContent>
        <CarouselNext className="size-8 rtl:rotate-180 p-0 border-none !bg-transparent [&>svg]:stroke-gray-dark [&>svg]:disabled:stroke-gray-400" />
      </Carousel>
    </>
  );
};

const CarouselWithThumbs_lg = ({
  data,
  t,
  duration,
  estimation,
  discount,
  priceOfToday,
}: {
  data?: Property;
  t: any;
  duration: number;
  estimation: EstimationType;
  discount: number;
  priceOfToday: { original: number; current: number };
}) => {
  const { onThumbClick, selectedIndex, setApiMain, setApiThumb } = useThumb();
  const isFavorite = useBoolean(data?.isFavorite);
  const isLoading = useBoolean(false);
  const locale = useLocale();
  const { data: cities } = useCities();

  return (
    <div className="relative overflow-hidden min-h-[230px] sm:min-h-[650px]">
      <Carousel
        setApi={setApiMain}
        opts={{ loop: true, direction: dir(locale) }}
        plugins={[Fade()]}
        className="rounded-2xl relative h-[230px] sm:h-[650px] overflow-hidden"
      >
        <CarouselContent>
          {data?.media
            ?.filter((p) => p.type === "IMAGE")
            ?.map((img, i) => (
              <ImageContainer {...img} key={i} />
            ))}
        </CarouselContent>
        <div className="absolute inset-0 px-6 lg:px-12 py-5 lg:py-10 flex gap-10 items-end w-full">
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0)_77.36%,_rgba(0,_0,_0,_0.4)_85.02%)] rounded-2xl" />
          <Carousel
            setApi={setApiThumb}
            orientation="vertical"
            opts={{
              containScroll: "keepSnaps",
              direction: dir(locale),
            }}
          >
            <CarouselPrevious className="size-8 relative ltr:translate-x-full rtl:-translate-x-full p-0 border-none hover:bg-black/20 disabled:bg-transparent bg-transparent" />
            <CarouselContent className="h-80 w-20 lg:w-28 space-y-2">
              {data?.media
                ?.filter((p) => p.type === "IMAGE")
                ?.map((img, i) => (
                  <ThumbnailContainer
                    i={i}
                    key={i}
                    img={img}
                    onThumbClick={onThumbClick}
                    selectedIndex={selectedIndex}
                  />
                ))}
            </CarouselContent>
            <CarouselNext className="size-8 mt-2 ltr:translate-x-full rtl:-translate-x-full p-0 border-none hover:bg-black/20 disabled:bg-transparent bg-transparent" />
          </Carousel>
          <div className="text-white space-y-3 flex-1 relative z-10">
            <div className="flex gap-4">
              {data?.ehjzleeOperated && (
                <Badge>{t("carousel.managedByEhjzlee")} </Badge>
              )}
              {discount ? (
                !data?.pricingRules?.length ? (
                  <Badge>
                    {t("carousel.discount")} {Math.abs(discount)}
                    {data?.pricingRules?.[0]?.type === "ABSOLUTE_DIFFERENCE"
                      ? t("propCard.current")
                      : "%"}
                  </Badge>
                ) : null
              ) : null}
            </div>
            <div className="flex gap-2 items-center">
              <View360 view360url={data?.view360Url} />
              <DisplayVideos
                videos={data?.media.filter((p) => p.type === "VIDEO")}
              />
            </div>
            {data?.pricingRules?.length ? (
              <Badge className="font-semibold">
                {t("carousel.discountPeriod", {
                  discount: `${Math.abs(discount)} ${
                    data?.pricingRules?.[0]?.type === "ABSOLUTE_DIFFERENCE"
                      ? t("propCard.current")
                      : "%"
                    }`,
                  endPeriod: format(
                    new Date(data?.pricingRules[0].endDate),
                    "dd/MM/yyyy"
                  ),
                })}
              </Badge>
            ) : null}
            <div className="flex flex-1 justify-between items-center gap-4 flex-wrap-reverse">
              <div className="flex gap-4 items-center max-sm:hidden">
                <UnitIcon
                  type={data?.classification}
                  className="size-10 [&_path]:fill-white"
                />
                {data ? (
                  <H2 className="max-lg:text-xl">{data?.title}</H2>
                ) : (
                  <Skeleton className="h-10 min-w-[300px]" />
                )}
                {data ? (
                  <p
                    dir={dir(locale)}
                    className="mr-10 flex items-center gap-4"
                  >
                    {!estimation
                      ? priceOfToday.current < priceOfToday.original && (
                          <del className="font-light text-lg">
                            {Number(priceOfToday.original)?.toLocaleString(
                              "en-US"
                            )}
                          </del>
                        )
                      : Number(estimation?.subtotalAfterDiscounts) <
                          Number(estimation?.subtotalBeforeDiscounts) && (
                          <del className="font-light text-lg">
                            {roundTo1Decimal(
                              Number(estimation?.subtotalBeforeDiscounts) /
                                duration
                            )?.toLocaleString("en-US")}
                          </del>
                        )}
                    <strong className="text-green-main text-4xl">
                      {estimation?.subtotalAfterDiscounts
                        ? roundTo1Decimal(
                            Number(estimation?.subtotalAfterDiscounts) /
                              duration
                          )?.toLocaleString("en-US")
                        : Number(priceOfToday.current)?.toLocaleString("en-US")}
                    </strong>
                    <span> {t("carousel.pricePerNight")}</span>
                  </p>
                ) : (
                  <Skeleton className="h-10 min-w-[200px]" />
                )}
              </div>
              <div className="flex gap-4 *:bg-black/50 *:rounded-full">
                <ChatBtn unitId={data?.id!} ownerCode={data?.owner?.code!} />
                <FavoriteBtn
                  isLoading={isLoading}
                  id={data?.id!}
                  isFavorite={isFavorite}
                />
              </div>
            </div>
            {data ? (
              <p className="mr-12">
                {data?.location?.address}،{" "}
                {
                  cities?.find((c) => c?.id === data?.cityId)?.[
                    locale === "ar" ? "arabicName" : "name"
                  ]
                }
              </p>
            ) : (
              <Skeleton className="h-6 w-[150px]" />
            )}
          </div>
        </div>
      </Carousel>
    </div>
  );
};

const ImageContainer = (img: MediaType) => {
  const [err, setErr] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  return (
    <CarouselItem
      className={`h-[230px] relative sm:h-[650px] overflow-hidden rounded-2xl flex justify-center items-center`}
    >
      <link rel="preload" as="image" href={`${img.url}?format=webp`} />
      {/* Show loader if loading */}
      {loading && !err && <CircularProgress size="lg" />}

      {/* Show warning icon if there's an error */}
      {err && <CircleAlert className="text-gray-500 size-10 sm:size-32" />}

      <Image
        fill
        src={`${img.thumbnailUrl}?format=webp`}
        alt={img.filename}
        className={cn("blur-3xl z-[-1]")}
      />

      <Image
        width={2000}
        height={2000}
        loading="lazy"
        src={`${img.url}?format=webp`}
        alt={img.filename}
        className={cn(
          "object-contain w-auto h-full absolute top-1/2 -translate-x-1/2 left-1/2 -translate-y-1/2"
        )}
        onLoad={() => setLoading(false)} // Update loading state on success
        onError={() => {
          setErr(true); // Update error state
          setLoading(false); // Stop the loader
        }}
      />
    </CarouselItem>
  );
};

const ThumbnailContainer = ({
  img,
  i,
  onThumbClick,
  selectedIndex,
}: {
  img: MediaType;
  i: number;
  selectedIndex: number;
  onThumbClick: (i: number) => void;
  size?: number;
}) => {
  const [err, setErr] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  return (
    <CarouselItem
      className="relative !basis-1/4 cursor-pointer flex justify-center items-center  !p-0 overflow-hidden rounded-2xl data-[selected=true]:border-2 data-[selected=true]:border-green-main"
      onClick={() => onThumbClick(i)}
      data-selected={i === selectedIndex}
    >
      {/* Show loader if loading */}
      {loading && !err && <CircularProgress size="sm" />}

      {/* Show warning icon if there's an error */}
      {err && <CircleAlert className="text-gray-500 size-10" />}

      <Image
        src={`${img?.thumbnailUrl}?format=webp`}
        alt={img?.filename}
        loading="lazy"
        width={100}
        height={100}
        className={cn("block size-full object-cover absolute inset-0")}
        onLoad={() => setLoading(false)} // Update loading state on success
        onError={() => {
          setErr(true); // Update error state
          setLoading(false); // Stop the loader
        }}
      />
    </CarouselItem>
  );
};
